import { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';

interface RingGaugeProps {
  value: number;
  maxValue?: number;
  label: string;
  benchmark?: number;
  color?: string;
  detail?: string;
  /** Extra content under the description (e.g. a BasisBadge). */
  footer?: React.ReactNode;
}

export default function RingGauge({
  value,
  maxValue = 100,
  label,
  benchmark,
  color,
  detail,
  footer,
}: RingGaugeProps) {
  const clamped = Math.max(0, Math.min(maxValue, value));
  const pct = clamped / maxValue;

  const fillColor = color || 'var(--secondary)';

  // SVG circle math
  const size = 80;
  const strokeWidth = 7;
  const r = (size - strokeWidth) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  const filled = circumference * pct;
  const gap = circumference - filled;

  // Animate on scroll into view
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.3 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const animatedFilled = visible ? filled : 0;
  const animatedGap = visible ? gap : circumference;

  return (
    <Card size="sm" className="flex-1 min-w-[120px] text-center">
      <CardContent className="flex flex-col items-center px-4 py-2.5">
        <div ref={ref}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="block">
          {/* Background track */}
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="var(--muted)"
            strokeWidth={strokeWidth}
          />
          {/* Value arc — starts at 12 o'clock via transform rotate(-90) */}
          {pct > 0 && (
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={fillColor}
              strokeWidth={strokeWidth}
              strokeDasharray={`${animatedFilled} ${animatedGap}`}
              strokeLinecap="round"
              transform={`rotate(-90 ${cx} ${cy})`}
              style={{ transition: 'stroke-dasharray 0.8s ease-out' }}
            />
          )}
          {/* Center text */}
          <text
            x={cx}
            y={cy}
            textAnchor="middle"
            dominantBaseline="central"
            className="fill-foreground"
            style={{
              fontSize: '18px',
              fontWeight: 700,
              opacity: visible ? 1 : 0,
              transition: 'opacity 0.5s ease-out 0.3s',
            }}
          >
            {value}
          </text>
        </svg>
        </div>
        <div className="mt-2.5 text-[13px] font-semibold text-foreground">{label}</div>
        {benchmark !== undefined && (
          <div className="mt-0.5 text-xs text-muted-foreground/80">Benchmark: {benchmark}</div>
        )}
        {detail && (
          <p className="mt-2.5 text-xs leading-normal text-muted-foreground text-pretty">{detail}</p>
        )}
        {footer}
      </CardContent>
    </Card>
  );
}
